import { XMarkIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useState } from "react";

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpModal = ({ isOpen, onClose }: HelpModalProps) => {
  const [expandedSection, setExpandedSection] = useState<"videos" | "images" | "realityscan" | "colmap">(
    "videos"
  );

  if (!isOpen) return null;

  const handleSectionClick = (section: "videos" | "images" | "realityscan" | "colmap") => {
    if (expandedSection !== section) {
      setExpandedSection(section);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 backdrop-blur-sm">
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/75"
        onClick={onClose}
      />

      <div className="relative bg-white dark:bg-zinc-900 rounded-xl w-full max-w-lg shadow-xl ring-1 ring-zinc-900/5 dark:ring-white/10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
            Help
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-zinc-400 dark:text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 hover:text-zinc-900 dark:hover:text-white transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-3">
          {/* Videos */}
          <div>
            <button
              onClick={() => handleSectionClick("videos")}
              className={`w-full flex items-center justify-between p-4 text-left rounded-lg transition-colors ${
                expandedSection === "videos"
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-400"
                  : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
              }`}
            >
              <span className="font-semibold">Instructions for video file</span>
              <ChevronUpIcon
                className={`w-5 h-5 transition-transform duration-200 ${
                  expandedSection === "videos" ? "rotate-0" : "rotate-180"
                }`}
              />
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                expandedSection === "videos"
                  ? "max-h-96 opacity-100 mt-2"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg">
                <ul className="space-y-2.5 text-sm text-zinc-700 dark:text-zinc-300">
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <div className="flex-1">
                      <span className="font-medium">
                        Supported video formats
                      </span>
                      <ul className="mt-2 ml-4 space-y-1.5">
                        <li className="flex gap-2">
                          <span className="text-zinc-600 dark:text-zinc-400">
                            .mp4 .mov .webm .avi
                          </span>
                        </li>
                      </ul>
                    </div>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>Disable HDR when capturing videos</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Capture a scene or object by going around in loops
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Ideally, capture in loops from 3 heights: phone at chest
                      level looking ahead, phone a bit above the head pointing a
                      bit down to the center of the scene, and finally from knee
                      level pointing upwards a bit.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>Capture slowly to reduce blur</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      You can either upload a normal/fisheye/equirectangular
                      video or a zip of videos
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Maximum the size of the video can be 5 gigabytes
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Images */}
          <div>
            <button
              onClick={() => handleSectionClick("images")}
              className={`w-full flex items-center justify-between p-4 text-left rounded-lg transition-colors ${
                expandedSection === "images"
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-400"
                  : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
              }`}
            >
              <span className="font-semibold">Instructions for images zip</span>
              <ChevronUpIcon
                className={`w-5 h-5 transition-transform duration-200 ${
                  expandedSection === "images" ? "rotate-0" : "rotate-180"
                }`}
              />
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                expandedSection === "images"
                  ? "max-h-96 opacity-100 mt-2"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg">
                <ul className="space-y-2.5 text-sm text-zinc-700 dark:text-zinc-300">
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <div className="flex-1">
                      <span className="font-medium">
                        Supported image formats
                      </span>
                      <ul className="mt-2 ml-4 space-y-1.5">
                        <li className="flex gap-2">
                          <span className="text-zinc-600 dark:text-zinc-400">
                            .jpg .jpeg .png
                          </span>
                        </li>
                      </ul>
                    </div>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Make sure the zip contains same kind of image formats
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      You can upload a zip of normal/fisheye/equirectangular
                      images
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Maximum the size of the images zip file can be 5 gigabytes
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* RealityScan */}
          <div>
            <button
              onClick={() => handleSectionClick("realityscan")}
              className={`w-full flex items-center justify-between p-4 text-left rounded-lg transition-colors ${
                expandedSection === "realityscan"
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-400"
                  : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
              }`}
            >
              <span className="font-semibold">Instructions for RealityScan</span>
              <ChevronUpIcon
                className={`w-5 h-5 transition-transform duration-200 ${
                  expandedSection === "realityscan" ? "rotate-0" : "rotate-180"
                }`}
              />
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                expandedSection === "realityscan"
                  ? "max-h-96 opacity-100 mt-2"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg">
                <ul className="space-y-2.5 text-sm text-zinc-700 dark:text-zinc-300">
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      In RealityScan, go to the Alignment menu, click Registration, and export in COLMAP Text Format.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Compress Cameras.txt / Images.txt / Points3D.txt / images folder into a single ZIP file and upload it.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Maximum the size of the images zip file can be 5 gigabytes
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* COLMAP */}
          <div>
            <button
              onClick={() => handleSectionClick("colmap")}
              className={`w-full flex items-center justify-between p-4 text-left rounded-lg transition-colors ${
                expandedSection === "colmap"
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-900 dark:text-emerald-400"
                  : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
              }`}
            >
              <span className="font-semibold">Instructions for Colmap</span>
              <ChevronUpIcon
                className={`w-5 h-5 transition-transform duration-200 ${
                  expandedSection === "colmap" ? "rotate-0" : "rotate-180"
                }`}
              />
            </button>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                expandedSection === "colmap"
                  ? "max-h-96 opacity-100 mt-2"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 rounded-lg">
                <ul className="space-y-2.5 text-sm text-zinc-700 dark:text-zinc-300">
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Colmap Binary Format data is supported.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Compress the images / images_2 / images_4 / images_8 / sparse folders into a single ZIP file and upload it.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-emerald-600 dark:text-emerald-400 mt-0.5">
                      •
                    </span>
                    <span>
                      Maximum the size of the images zip file can be 5 gigabytes
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-zinc-200 dark:border-zinc-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
